import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * The Anrede, as a code (backend #682 decision 6, backend PR #697).
 *
 * Until that slice the field held German prose — „Herr", „Frau", „Divers", „Firma" — typed into a
 * free-text box. Prose can only ever exist in one language, and this field is printed on the
 * invoice PDF, so the platform now stores a key and every client renders the word itself. See
 * #165 / #177 for why the field is called `salutation` at all: it is the Anrede, not the postal
 * address, which is `street` / `zip` / `city` beside it.
 *
 * **Lenient when reading, strict when writing** — the rule the backend's own renderer follows.
 * A value that is not one of the four codes is somebody's data written before the codes existed,
 * so {@link salutationLabel} shows it as-is rather than blanking it, and
 * {@link salutationOptionsFor} keeps it in the picker so the user can see what is stored. Writing
 * it back is another matter: both backend write paths now reject anything else with the coded
 * error `salutationInvalid`, which is why {@link salutationCodeValidator} refuses it here, at the
 * field, instead of letting the user discover it as a failed save.
 *
 * The backend migrated the four German words it had written itself
 * (`SalutationToCode_682`), so in practice an unknown value is only ever something a user typed.
 */
export enum SalutationCode {
  Mr = 'mr',
  Ms = 'ms',
  Diverse = 'diverse',
  Company = 'company',
}

/** The word for each code, in the language of this bundle. */
const LABELS: Readonly<Record<SalutationCode, string>> = Object.freeze({
  [SalutationCode.Mr]: $localize`:@@salutation.mr:Herr`,
  [SalutationCode.Ms]: $localize`:@@salutation.ms:Frau`,
  [SalutationCode.Diverse]: $localize`:@@salutation.diverse:Divers`,
  [SalutationCode.Company]: $localize`:@@salutation.company:Firma`,
});

/** One option per code, in the order the picker offers them. */
export const SALUTATION_OPTIONS: ReadonlyArray<{ code: SalutationCode; label: string }> =
  Object.freeze(
    Object.values(SalutationCode).map((code) => ({ code, label: LABELS[code] })),
  );

/** True for exactly the four codes — the comparison is case-sensitive, as the backend's is. */
export function isSalutationCode(value: unknown): value is SalutationCode {
  return typeof value === 'string' && value in LABELS;
}

/**
 * The word to show for a stored value. Empty for an unset salutation — an unset one renders no
 * line at all rather than a placeholder — and the raw value for anything unrecognised.
 */
export function salutationLabel(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return isSalutationCode(value) ? LABELS[value] : value;
}

/**
 * The picker's options for a form holding `current`. Normally the four codes; when the stored
 * value is something else, it is appended as its own option so the user sees their own data
 * instead of an empty box. {@link salutationCodeValidator} still stops them saving it.
 */
export function salutationOptionsFor(
  current: string | null | undefined,
): ReadonlyArray<{ code: string; label: string }> {
  if (!current || isSalutationCode(current)) {
    return SALUTATION_OPTIONS;
  }
  return [...SALUTATION_OPTIONS, { code: current, label: current }];
}

/**
 * Rejects a non-empty value that is not one of the four codes, as `{ salutationCode: true }`.
 *
 * An empty value is left to `Validators.required`, so a missing Anrede keeps reporting itself as
 * missing rather than as invalid.
 */
export const salutationCodeValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value;
  if (!value || isSalutationCode(value)) {
    return null;
  }
  return { salutationCode: true };
};
